import { useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import NumberField from './NumberField';
import ResultPanel from './ResultPanel';
import ToolLayout from './ToolLayout';
import { formatNumber, parseNumber } from '../utils/numbers';

const nozzleTypes = [
  { value: 'venturi', label: 'Venturi nozzle' },
  { value: 'straight', label: 'Straight bore nozzle' },
];

const abrasiveTypes = [
  { value: 'garnet', label: 'Garnet' },
  { value: 'aluminum_oxide', label: 'Aluminium oxide' },
  { value: 'steel_grit', label: 'Steel grit' },
  { value: 'glass_beads', label: 'Glass beads' },
];

function SandBlasting() {
  const [nozzleType, setNozzleType] = useState('venturi');
  const [abrasiveType, setAbrasiveType] = useState('garnet');
  const [nozzleDiameter, setNozzleDiameter] = useState('');
  const [pressure, setPressure] = useState('');
  const [airFlow, setAirFlow] = useState('');
  const [abrasiveFlow, setAbrasiveFlow] = useState('');

  const { result, error } = useMemo(() => {
    const diameter = parseNumber(nozzleDiameter);
    const pressureNum = parseNumber(pressure);
    const air = parseNumber(airFlow);
    const abrasive = parseNumber(abrasiveFlow);

    if (diameter == null || pressureNum == null || air == null || abrasive == null) {
      return { result: null, error: '' };
    }
    if (diameter <= 0 || pressureNum <= 0) {
      return { result: null, error: 'Diameter and pressure must be greater than zero' };
    }

    const nozzleAreaMm2 = Math.PI * (diameter / 2) ** 2;
    const efficiency = (abrasive * air) / (nozzleAreaMm2 * pressureNum);
    return { result: { efficiency }, error: '' };
  }, [nozzleDiameter, pressure, airFlow, abrasiveFlow]);

  return (
    <ToolLayout title="Sandblasting">
      <Card>
        <CardContent>
          <Typography color="text.secondary" paragraph>
            Estimate blasting efficiency from nozzle size, pressure, air flow, and abrasive flow.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl>
                <InputLabel>Nozzle type</InputLabel>
                <Select value={nozzleType} label="Nozzle type" onChange={(event) => setNozzleType(event.target.value)}>
                  {nozzleTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl>
                <InputLabel>Abrasive type</InputLabel>
                <Select
                  value={abrasiveType}
                  label="Abrasive type"
                  onChange={(event) => setAbrasiveType(event.target.value)}
                >
                  {abrasiveTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Nozzle diameter (mm)" value={nozzleDiameter} onChange={setNozzleDiameter} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Pressure (bar)" value={pressure} onChange={setPressure} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Air flow (m³/min)" value={airFlow} onChange={setAirFlow} min={0} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <NumberField label="Abrasive flow (kg/min)" value={abrasiveFlow} onChange={setAbrasiveFlow} min={0} />
            </Grid>
          </Grid>
          {error && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          )}
          {result && (
            <ResultPanel
              items={[{ label: 'Blasting efficiency', value: `${formatNumber(result.efficiency, 2)} kg/m²·min`, fullWidth: true }]}
            />
          )}
        </CardContent>
      </Card>
      <Divider sx={{ my: 2 }} />
      <Typography variant="body2" color="text.secondary">
        Efficiency = (abrasive flow × air flow) / (nozzle area × pressure). Nozzle and abrasive type are recorded for reference and do not change this estimate.
      </Typography>
    </ToolLayout>
  );
}

export default SandBlasting;
